import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Box, ButtonProps, Divider, IconButton, ListItem, ListItemButton, ListItemText, ListSubheader, Menu, MenuItem, Tooltip, TooltipProps, Typography, useTheme } from '@mui/material'
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state'
import { setTimeBucketsOpen, updateChartTimeBucket } from '../../reducer/chart.reducer'
import { selectChartTimeBucket, selectTimebucketsOpen } from '../../selectors/chart'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'
import CustomTimeBucketModal from './CustomTimeBucketModal'
import { darkBg, white } from '../../assets/colors'
import { ShortcutsCategories, ShortcutType } from '../KeyboardShortcuts/Shortcuts.types'
import { CustomTimeBucketShortcutsKeyName } from '../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../hooks/useShortcuts'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import { selectMultiStrikeChartTimebucket } from '../../selectors/multiStrikeChart'
import { setMultiStrikeChartTimeBucket } from '../../reducer/multiStrikeChart.reducer'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import BinIcon from '../../assets/icons/BinIcon'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import { Star } from '@mui/icons-material'
import { updateUserPreferences } from '../../services/apis/user'
import { selectCustomTimeBuckets, selectFavouriteTimeBuckets } from '../../selectors/user'
import PlusIcon from '../../assets/icons/PlusIcon'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'
import StarOutlinedIcon from '../../assets/icons/StarOutlinedIcon'
import { getTimeBucketDisplayName } from '../../util/timeBuckets'
import { selectIsEmbedded } from '../../selectors/device.ts'
import { callNative } from '../../util/nativeBridge'
import { useUser } from '../../context/UserContext.tsx'
import { FeatureCategory, FeatureName, FeatureSubCategory, getTimeBucketFeatureName } from '../adminUserRestrictions/Restrictions.constants.ts'
import { Group, setFeatureRestrictionDialog } from '../../reducer/user.reducer.ts'

interface TimeBucketsProps {
    id?: string
    viewKey: string
    disabled?: boolean
    multiStrikeTimebucket?: boolean
    buttonProps?: ButtonProps
    tooltipProps?: Omit<TooltipProps, 'children'>
    registerShortcut: boolean
    showFavorites: boolean
}

export type TimeBucket = SuperChartConfiguration_TimeBucket & {
    custom: boolean
    secondsValue: number
}

const TimeBucketsArray = [
    {
        label: 'SECONDS',
        value: TimeBucket_TimeSeries.SECOND
    },
    {
        label: 'MINUTES',
        value: TimeBucket_TimeSeries.MINUTE
    },
    {
        label: 'HOURS',
        value: TimeBucket_TimeSeries.HOUR
    },
    {
        label: 'DAYS',
        value: TimeBucket_TimeSeries.DAY
    },
    {
        label: 'WEEKS',
        value: TimeBucket_TimeSeries.WEEK
    },
    {
        label: 'MONTHS',
        value: TimeBucket_TimeSeries.MONTH
    }
]

export function getSecondsValue(bucket: SuperChartConfiguration_TimeBucket) {
    let multiplier = 1
    switch (bucket.timeSeries) {
        case TimeBucket_TimeSeries.SECOND:
            multiplier = 1
            break
        case TimeBucket_TimeSeries.MINUTE:
            multiplier = 60
            break
        case TimeBucket_TimeSeries.HOUR:
            multiplier = 3600
            break
        case TimeBucket_TimeSeries.DAY:
            multiplier = 86400
            break
        case TimeBucket_TimeSeries.WEEK:
            multiplier = 604800
            break
        case TimeBucket_TimeSeries.MONTH:
            multiplier = 2592000
            break
        case TimeBucket_TimeSeries.YEAR:
            multiplier = 31536000
            break
    }
    return multiplier * bucket.timeValue
}

const TimeBuckets = React.memo(({ disabled = false, multiStrikeTimebucket = false, viewKey, buttonProps = {}, tooltipProps, registerShortcut, id, showFavorites = false }: TimeBucketsProps) => {
    const dispatch = useDispatch()
    const {
        user: { hasAccess }
    } = useUser()
    const customTimeBucketAccess = hasAccess(FeatureCategory.CHART, FeatureSubCategory.TIMEBUCKET, FeatureName.CUSTOM_TIME_BUCKETS)
    const timeBuckets: SuperChartConfiguration_TimeBucket[] = useSelector((state: RootState) => state.chartStore.timeBuckets)
    const customTimeBuckets = useSelector(selectCustomTimeBuckets)
    const isEmbedded = useSelector(selectIsEmbedded)
    const mergedTimeBuckets = useMemo(() => {
        const buckets: TimeBucket[] = []
        timeBuckets.forEach((bucket) => {
            buckets.push({
                ...bucket,
                custom: false,
                secondsValue: getSecondsValue(bucket)
            })
        })
        customTimeBuckets.forEach((bucket) => {
            buckets.push({
                ...bucket,
                custom: true,
                secondsValue: getSecondsValue(bucket)
            })
        })
        return buckets
    }, [timeBuckets, customTimeBuckets])
    const [openCustomTimeBucket, setOpenCustomTimeBucket] = useState(false)
    const chartTimeBucket = useSelector(selectChartTimeBucket(viewKey))
    const multiStrikesChartTimeBucket = useSelector(selectMultiStrikeChartTimebucket)
    const selectedChartBucket = useMemo(() => (multiStrikeTimebucket ? multiStrikesChartTimeBucket : chartTimeBucket), [chartTimeBucket, multiStrikeTimebucket, multiStrikesChartTimeBucket])
    const timeBucketsOpen = useSelector(selectTimebucketsOpen)
    const [timeBucketModalOpen, setTimeBucketModalOpen] = useState(false)
    const theme = useTheme()
    const { push } = useUndoRedoContext()
    const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number }>({
        top: 0,
        left: 0
    })
    const anchorRef = useRef<HTMLButtonElement>(null)

    const handleRemoveBucket = useCallback(
        (bucketCode: string) => {
            const newCustomTimeBuckets = customTimeBuckets.filter((bucket) => bucket.code !== bucketCode)
            updateUserPreferences({
                body: {
                    customTimeBuckets: newCustomTimeBuckets
                }
            })
        },
        [customTimeBuckets]
    )
    const handleClose = useCallback(() => {
        setTimeBucketModalOpen(false)
        setAnchorPosition({ top: 0, left: 0 })
        dispatch(setTimeBucketsOpen(false))
    }, [dispatch])
    const favorites = useSelector(selectFavouriteTimeBuckets)
    const [defaultBucket, setDefaultBucket] = useState<SuperChartConfiguration_TimeBucket>()
    const timeUnitToMinutes = (timeBucket: string) => {
        const unitMap: Record<string, number> = {
            s: 1 / 60,
            m: 1,
            h: 60,
            d: 1440,
            D: 1440,
            W: 7 * 1440,
            M: 30 * 1440,
            Y: 365 * 1440
        }
        const match = timeBucket.match(/^(\d+)([smhdDWMY])$/)
        if (!match) return Infinity
        const [, value, unit] = match
        return parseFloat(value) * unitMap[unit]
    }
    useEffect(() => {
        if (!selectedChartBucket) return
        if (showFavorites) {
            if (defaultBucket) {
                if (favorites.includes(getTimeBucketDisplayName({ timeSeries: defaultBucket.timeSeries, value: defaultBucket.timeValue }) ?? '')) {
                    setDefaultBucket(undefined)
                } else if (!favorites.includes(getTimeBucketDisplayName({ timeSeries: selectedChartBucket.timeSeries, value: selectedChartBucket.timeValue }) ?? '')) {
                    setDefaultBucket(selectedChartBucket)
                }
            } else {
                if (!favorites.includes(getTimeBucketDisplayName({ timeSeries: selectedChartBucket.timeSeries, value: selectedChartBucket.timeValue }) ?? '')) {
                    setDefaultBucket(selectedChartBucket)
                }
            }
        }
    }, [showFavorites, selectedChartBucket, favorites])

    useEffect(() => {
        if (!selectedChartBucket) return
        if (!showFavorites) {
            setDefaultBucket(selectedChartBucket)
        } else if (favorites.length === 0) {
            setDefaultBucket(selectedChartBucket)
        }
    }, [selectedChartBucket])

    const toggleFavorite = useCallback(
        (bucket: SuperChartConfiguration_TimeBucket) => {
            const favoriteKey = getTimeBucketDisplayName({ timeSeries: bucket.timeSeries, value: bucket.timeValue })
            if (favoriteKey) {
                const newFavorites = [...favorites]
                if (newFavorites.includes(favoriteKey)) {
                    newFavorites.splice(newFavorites.indexOf(favoriteKey), 1)
                } else {
                    newFavorites.push(favoriteKey)
                }
                const sortedFavorites = newFavorites.sort((a, b) => timeUnitToMinutes(a) - timeUnitToMinutes(b))
                updateUserPreferences({
                    body: {
                        favoriteTimeBuckets: sortedFavorites
                    }
                })
            }
        },
        [favorites]
    )

    const handleOpenCustomTimeBucket = useCallback(() => {
        if (!hasAccess(FeatureCategory.CHART, FeatureSubCategory.TIMEBUCKET, FeatureName.CUSTOM_TIME_BUCKETS)) {
            dispatch(
                setFeatureRestrictionDialog({
                    open: true,
                    feature: 'Custom Timebuckets',
                    featureRestrictionCode:'CUSTOM_TIME_BUCKETS',
                    group: Group.CHART
                })
            )
            return
        }
        setOpenCustomTimeBucket(true)
    }, [hasAccess, dispatch])

    const handleChangeBucket = useCallback(
        (bucket: SuperChartConfiguration_TimeBucket) => {
            handleClose()
            const timeBucketFeature = getTimeBucketFeatureName(bucket.timeSeries)
            if (!hasAccess(FeatureCategory.CHART, FeatureSubCategory.TIMEBUCKET, timeBucketFeature)) {
                dispatch(
                    setFeatureRestrictionDialog({
                        open: true,
                        feature: bucket.name + ' timebucket',
                        group: Group.CHART
                    })
                )
                return
            }

            const bucketName = getTimeBucketDisplayName({ timeSeries: bucket.timeSeries, value: bucket.timeValue }) ?? ''
            const isFavorite = favorites.includes(bucketName)
            if (!selectedChartBucket || selectedChartBucket.timeSeries !== bucket.timeSeries || selectedChartBucket.timeValue !== bucket.timeValue) {
                if (multiStrikeTimebucket) {
                    dispatch(setMultiStrikeChartTimeBucket(bucket))
                } else {
                    dispatch(updateChartTimeBucket({ viewKey, timeBucket: bucket }))
                }
                if (selectedChartBucket) {
                    push(
                        [
                            {
                                chartId: viewKey,
                                currentData: bucket,
                                previousData: selectedChartBucket
                            }
                        ],
                        `Time Bucket (${bucket.name})`,
                        ActionType.TIME_BUCKET
                    )
                }
                if (!isFavorite) {
                    setDefaultBucket(bucket)
                }
            }
        },
        [handleClose, favorites, selectedChartBucket, multiStrikeTimebucket, push, viewKey, dispatch, hasAccess]
    )

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        if (isEmbedded) {
            callNative('onSeriesMetaDataTimeBucketMenuClicked')
        } else {
            setTimeBucketModalOpen(true)
            const rect = event.currentTarget.getBoundingClientRect()
            setAnchorPosition({ top: rect.bottom, left: rect.left })
        }
    }, [])

    const shortcuts: ShortcutType = useMemo(
        () => ({
            [CustomTimeBucketShortcutsKeyName.openCustomTimeBucketIntervalDialog]: () => {
                AnalyticsManager.logEvent('Time Bucket Menu Opened', { source: 'keyboard' })
                if (anchorRef.current) {
                    const rect = anchorRef.current.getBoundingClientRect()
                    setAnchorPosition({ top: rect.bottom, left: rect.left })
                }
            }
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [favorites]
    )

    useShortcuts(ShortcutsCategories.CUSTOM_TIME_INTERVAL, shortcuts, registerShortcut)

    const handleKeyDown = (event: any) => {
        const { key } = event
        const items = document.querySelectorAll('.time-interval')
        let index = Array.prototype.indexOf.call(items, document.activeElement)

        if (key === 'ArrowDown') {
            index = (index + 1) % items.length
            ;(items[index] as HTMLElement).focus()
        } else if (key === 'ArrowUp') {
            index = (index - 1 + items.length) % items.length
            ;(items[index] as HTMLElement).focus()
        }
    }

    const container = useSelector(selectMultiPaneFullScreen)
    const handleFavoriteClick = useCallback(
        (bucketName: string) => {
            const selectedBucket = [...timeBuckets, ...customTimeBuckets].find((b) => getTimeBucketDisplayName({ timeSeries: b.timeSeries, value: b.timeValue }) === bucketName)
            if (!selectedBucket) {
                console.warn('No matching time bucket found for:', bucketName)
                return
            }
            if (selectedChartBucket) {
                handleChangeBucket(selectedBucket)
            }
        },
        [customTimeBuckets, handleChangeBucket, selectedChartBucket, timeBuckets]
    )
    return (
        <PopupState variant="popover" popupId="time-buckets-popup-menu" parentPopupState={undefined} disableAutoFocus={false}>
            {(popupState) => (
                <div style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto' }}>
                    {showFavorites &&
                        favorites.map((bucket: any) => (
                            <Tooltip key={bucket} title={`Time Bucket: ${bucket}`} arrow placement="bottom">
                                <ExtraSmallButton
                                    onClick={() => handleFavoriteClick(bucket as any)}
                                    key={bucket}
                                    size="small"
                                    sx={{
                                        backgroundColor: (theme) =>
                                            favorites.length > 1 && selectedChartBucket && getTimeBucketDisplayName({ timeSeries: selectedChartBucket.timeSeries, value: selectedChartBucket.timeValue }) === bucket
                                                ? theme.palette.background.default
                                                : 'inherit'
                                    }}
                                    data-event={'Candle Time Bucket Changed'}
                                    data-event-selected_bucket={bucket}
                                >
                                    <Typography width="max-width" variant={'body2'}>
                                        {bucket}
                                    </Typography>
                                </ExtraSmallButton>
                            </Tooltip>
                        ))}
                    {defaultBucket && tooltipProps ? (
                        <NonInteractiveTooltip {...tooltipProps}>
                            <Tooltip
                                title={
                                    <span style={{ fontSize: '12px' }}>
                                        Time Bucket:{' '}
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                padding: '2px 6px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backgroundColor: 'grey',
                                                borderRadius: '4px',
                                                margin: '0 2px'
                                            }}
                                        >
                                            ,
                                        </Box>
                                    </span>
                                }
                                arrow
                                placement="top"
                            >
                                <span style={{ display: 'inline-flex' }}>
                                    <ExtraSmallButton
                                        size="small"
                                        id={id}
                                        color="inherit"
                                        {...bindTrigger(popupState)}
                                        disabled={disabled}
                                        onClick={handleClick}
                                        ref={anchorRef}
                                        data-event={'StockBar TimeBuckets Menu Opened'}
                                        {...buttonProps}
                                    >
                                        <Typography width="24px" variant={'body2'}>
                                            {getTimeBucketDisplayName({ timeSeries: defaultBucket.timeSeries, value: defaultBucket.timeValue })}
                                        </Typography>
                                    </ExtraSmallButton>
                                </span>
                            </Tooltip>
                        </NonInteractiveTooltip>
                    ) : defaultBucket ? (
                        <Tooltip
                            title={
                                <span style={{ fontSize: '12px' }}>
                                    Time Bucket:
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            padding: '2px 6px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            backgroundColor: 'grey',
                                            borderRadius: '4px',
                                            margin: '0 2px'
                                        }}
                                    >
                                        {getTimeBucketDisplayName({ timeSeries: defaultBucket.timeSeries, value: defaultBucket.timeValue })}
                                    </Box>
                                </span>
                            }
                            arrow
                            placement="top"
                        >
                            <span style={{ display: 'inline-flex' }}>
                                <ExtraSmallButton
                                    id={id}
                                    size="small"
                                    sx={{
                                        backgroundColor: (theme) =>
                                            favorites.length > 1 &&
                                            selectedChartBucket &&
                                            getTimeBucketDisplayName({ timeSeries: selectedChartBucket.timeSeries, value: selectedChartBucket.timeValue }) ===
                                                getTimeBucketDisplayName({ timeSeries: defaultBucket.timeSeries, value: defaultBucket.timeValue })
                                                ? theme.palette.background.default
                                                : 'inherit'
                                    }}
                                    {...bindTrigger(popupState)}
                                    disabled={disabled}
                                    onClick={showFavorites && favorites.length >= 1 ? () => handleChangeBucket(defaultBucket) : handleClick}
                                    ref={anchorRef}
                                    data-event={'StockBar TimeBuckets Menu Opened'}
                                    {...buttonProps}
                                >
                                    <Typography width="24px" lineHeight="24px" variant="body2">
                                        {getTimeBucketDisplayName({ timeSeries: defaultBucket.timeSeries, value: defaultBucket.timeValue })}
                                    </Typography>
                                </ExtraSmallButton>
                            </span>
                        </Tooltip>
                    ) : null}
                    {showFavorites && favorites.length >= 1 && (
                        <ExtraSmallButton ref={anchorRef} size="small" color="inherit" {...bindTrigger(popupState)} onClick={handleClick} sx={{ padding: '4px 0px', minWidth: '24px' }}>
                            {timeBucketModalOpen ? <ThinArrowUpIcon fontSize={'small'} /> : <ThinArrowDownIcon fontSize={'small'} />}
                        </ExtraSmallButton>
                    )}
                    {!isEmbedded && (
                        <Menu
                            onKeyDown={handleKeyDown}
                            {...bindPopover(popupState)}
                            elevation={3}
                            anchorReference="anchorPosition"
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                            anchorPosition={anchorPosition}
                            container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                            open={anchorPosition.top !== 0 || anchorPosition.left !== 0}
                            onClick={popupState.close}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                            transitionDuration={0}
                            onClose={handleClose}
                        >
                            <MenuItem
                                dense
                                onClick={() => {
                                    handleClose()
                                    handleOpenCustomTimeBucket()
                                }}
                                data-event={'Add custom interval selected'}
                            >
                                <ListItemText>
                                    <PlusIcon sx={{ margin: '0 4px 0 -4px' }} fontSize={'small'} />
                                    Add custom interval
                                </ListItemText>
                            </MenuItem>
                            {TimeBucketsArray.map((option) => {
                                const filteredBuckets = mergedTimeBuckets.filter((bucket) => bucket?.timeSeries === option.value).sort((a, b) => (a?.secondsValue ?? 0) - (b?.secondsValue ?? 0))
                                return (
                                    <div key={option.value}>
                                        <Divider
                                            style={{
                                                backgroundColor: theme.palette.mode === 'dark' ? '#434651' : '#e0e3eb'
                                            }}
                                        />
                                        <ListSubheader
                                            sx={{
                                                lineHeight: '24px',
                                                backgroundColor: 'unset',
                                                fontSize: 11,
                                                opacity: 0.5,
                                                borderTop: 1,
                                                borderColor: theme.palette.mode === 'dark' ? darkBg : white
                                            }}
                                            key={option.value}
                                        >
                                            {option.label}
                                        </ListSubheader>
                                        {filteredBuckets.map((bucket, i) => {
                                            if (!bucket || bucket.timeSeries == null) return null
                                            const selected = selectedChartBucket?.timeSeries === bucket.timeSeries && selectedChartBucket?.timeValue === bucket.timeValue
                                            const favoriteKey = getTimeBucketDisplayName({ timeSeries: bucket.timeSeries, value: bucket.timeValue })
                                            const isFavorite = favorites.includes(favoriteKey ?? '')
                                            const timeBucketFeature = getTimeBucketFeatureName(bucket.timeSeries)
                                            const isEnabled = hasAccess(FeatureCategory.CHART, FeatureSubCategory.TIMEBUCKET, timeBucketFeature)
                                            return (
                                                <ListItem
                                                    key={bucket.code}
                                                    sx={{
                                                        '&:hover .MuiIconButton-root': {
                                                            visibility: 'visible'
                                                        },
                                                        '&:hover .star-icon': { opacity: 1, visibility: 'visible' },
                                                        '& .star-icon': {
                                                            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
                                                            opacity: timeBucketsOpen || isFavorite ? 1 : 0,
                                                            visibility: timeBucketsOpen || isFavorite ? 'visible' : 'hidden'
                                                        }
                                                    }}
                                                    secondaryAction={ isEnabled &&
                                                        <IconButton edge="end" aria-label="comments" onClick={() => toggleFavorite(bucket)}>
                                                            <span
                                                                className="star-icon"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    height: '24px'
                                                                }}
                                                            >
                                                                {bucket.custom && (
                                                                    <IconButton
                                                                        component="span"
                                                                        style={{ padding: '2px' }}
                                                                        sx={{ visibility: 'hidden' }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            handleRemoveBucket(bucket.code)
                                                                        }}
                                                                    >
                                                                        <BinIcon fontSize={'small'} />
                                                                    </IconButton>
                                                                )}
                                                                {isFavorite ? <Star /> : <StarOutlinedIcon />}
                                                            </span>
                                                        </IconButton>
                                                    }
                                                    disablePadding
                                                >
                                                    <ListItemButton
                                                        id={option.value === TimeBucket_TimeSeries.SECOND && bucket.code === 'ONE_SECOND' ? 'one-second-time-bucket-menu-item' : ''}
                                                        className="time-interval"
                                                        selected={selected}
                                                        onClick={() => handleChangeBucket(bucket)}
                                                        data-event={'Candle Time Bucket Changed'}
                                                        data-event-selected_bucket={bucket.name}
                                                        // disabled={!isEnabled}
                                                        sx={{
                                                            py: '2px'
                                                        }}
                                                    >
                                                        <ListItemText primary={bucket.name}/>
                                                    </ListItemButton>
                                                </ListItem>
                                            )
                                        })}
                                    </div>
                                )
                            })}
                        </Menu>
                    )}
                    {!isEmbedded && (
                        <CustomTimeBucketModal
                            open={openCustomTimeBucket}
                            handleClose={() => {
                                setOpenCustomTimeBucket(false)
                            }}
                            handleChangeBucket={handleChangeBucket}
                        />
                    )}
                </div>
            )}
        </PopupState>
    )
})

TimeBuckets.displayName = 'TimeBuckets'

export default TimeBuckets
